import { Button, Collapse, Group, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconAlertTriangle } from '@tabler/icons-react';
import { use } from 'react';

import { ChatContext } from '../contexts/ChatContext';
import type { ChatMessageProps } from '../types';
import { hasRole } from '../utils';
import classes from './ToolMessage.module.css';
import { useChatTools } from '#components/chat/contexts/ChatToolContext';
import { ChatMessage } from '#components/chat/messages/ChatMessage';
import type { ToolComponentProps } from '#components/chat/tool';
import { PropertyTable } from '#components/layout/PropertyTable';

export function ToolMessage<MetadataType>({
  id,
  timestamp,
  content,
  tool_call_id: toolCallId,
}: ChatMessageProps<'tool', MetadataType>) {
  const { messages } = use(ChatContext);
  const { toolsByName } = useChatTools();

  const sourceToolCall = messages
    .map((it) => (hasRole(it, 'assistant') ? it.message.tool_calls?.find((it) => it.id === toolCallId) : undefined))
    .find((it) => it);

  const functionName = sourceToolCall?.function.name;
  const tool = functionName ? toolsByName.get(functionName) : undefined;
  if (!tool) {
    return FallbackToolComponent({ output: JSON.parse(content), sourceToolCall });
  }
  const ToolComponent = tool.component ?? FallbackToolComponent;
  const output = JSON.parse(content); // TODO: validate output?

  return <ToolComponent id={id} output={output} sourceToolCall={sourceToolCall} timestamp={timestamp} />;
}

function FallbackToolComponent({ output, sourceToolCall }: Partial<ToolComponentProps>) {
  const [opened, { toggle }] = useDisclosure(false);
  return (
    <ChatMessage role="assistant" kind="error">
      <div className={classes.fallback}>
        <Group gap="xs">
          <IconAlertTriangle size={16} />
          <Text fz="xs" tt="uppercase" fw="bold">
            Tool component not found.
          </Text>

          <Button variant="light" size="xs" onClick={toggle} color="white">
            Show response object
          </Button>
        </Group>

        <Collapse in={opened}>
          <PropertyTable data={{ output, sourceToolCall }} />
        </Collapse>
      </div>
    </ChatMessage>
  );
}
